import { useCallback, useEffect, useSyncExternalStore } from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "qubbase-tasks-theme";
const media = () => window.matchMedia("(prefers-color-scheme: dark)");
const listeners = new Set<() => void>();

function readPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

let preference: ThemePreference =
  typeof window === "undefined" ? "system" : readPreference();

const resolve = (p: ThemePreference): ResolvedTheme =>
  p === "system" ? (media().matches ? "dark" : "light") : p;

/** Sets data-theme on <html>; the colour tokens in index.css switch on it. */
export function applyTheme(p: ThemePreference = preference) {
  const resolved = resolve(p);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;
}

function setPreference(p: ThemePreference) {
  preference = p;
  try {
    if (p === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, p);
  } catch {
    // storage unavailable (private mode): the choice still applies for this visit
  }
  applyTheme(p);
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** Current preference (light / dark / system), the theme actually shown, and a setter. */
export function useTheme() {
  // The snapshot includes the resolved theme so an OS switch re-renders while on "system".
  const snapshot = useSyncExternalStore(
    subscribe,
    () => `${preference}:${resolve(preference)}`,
  );
  const [pref, resolved] = snapshot.split(":") as [
    ThemePreference,
    ResolvedTheme,
  ];

  // Follow the OS setting while the preference is "system".
  useEffect(() => {
    const mq = media();
    const onChange = () => {
      if (preference === "system") {
        applyTheme("system");
        listeners.forEach((l) => l());
      }
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const set = useCallback((p: ThemePreference) => setPreference(p), []);
  return { preference: pref, resolved, setPreference: set };
}
